import { BackgroundOverlay } from '@/components/Tile';
import { LibraryItem } from '@d-id/types';
import styled from '@emotion/styled';
import { ReactNode } from 'react';
import { VideoTileOverlay } from './VideoTileOverlay';

export const CheckboxContainer = styled.div`
    opacity: 0;
    position: absolute;
    top: 1rem;
    left: 1rem;
    z-index: 5;
    transition: opacity 0.2s ease-in-out;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow-y: hidden;
`;

const ImageContainer = styled.div<{
    hovered: boolean;
    disableHover?: boolean;
    showSelectBorder?: boolean;
    selectCursor?: 'default' | 'not-allowed' | 'pointer';
}>`
    border-radius: 12px;
    border: ${({ theme, showSelectBorder }) =>
        showSelectBorder ? `2px solid ${theme.colors.secondary[900]}` : `1px solid ${theme.colors.black[300]}`};
    cursor: ${({ selectCursor }) => selectCursor || 'pointer'};
    width: 100%;
    aspect-ratio: 16/10;
    position: relative;
    overflow: hidden;

    ${({ hovered }) =>
        hovered &&
        `
        .BackgroundOverlay {
            opacity: 0.5;
            transform: scale(105%);
        }
    
        .ControlsContainer {
            opacity: 1;
        }
    `}

    ${({ disableHover }) =>
        !disableHover &&
        `
        :hover .BackgroundOverlay {
            opacity: 0.5;
            transform: scale(105%);
        }

        :hover .ControlsContainer {
            opacity: 1;
        }
    `}
`;

const ControlsContainer = styled.div`
    opacity: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 2;
    transition: all 0.2s ease-in-out;
`;

interface CoreVideoTileProps {
    item: LibraryItem;
    id?: string;
    hovered: boolean;
    isDone: boolean;
    isError: boolean;
    isLoading: boolean;
    badge?: ReactNode;
    controls?: ReactNode;
    tileInfo: ReactNode;
    checkbox?: ReactNode;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    disableHover?: boolean;
    showSelectBorder?: boolean;
    selectCursor?: 'default' | 'not-allowed' | 'pointer';
}

export const CoreVideoTile = ({
    item,
    id,
    hovered,
    isDone,
    isError,
    isLoading,
    badge,
    controls,
    tileInfo,
    checkbox,
    onClick,
    disableHover = false,
    showSelectBorder = false,
    selectCursor,
}: CoreVideoTileProps) => {
    return (
        <Container>
            <ImageContainer
                id={id}
                data-testid={`video-tile-image-container-${item.name}`}
                onClick={onClick}
                hovered={hovered}
                disableHover={disableHover}
                showSelectBorder={showSelectBorder}
                selectCursor={selectCursor}>
                <BackgroundOverlay bg={item.thumbnail_url} isLoading={!isDone} disabled={isError} />
                <VideoTileOverlay isDone={isDone} isError={isError} item={item} isLoading={isLoading} />
                {checkbox}
                {controls && <ControlsContainer className="ControlsContainer">{controls}</ControlsContainer>}
                {isDone && badge}
            </ImageContainer>
            {tileInfo}
        </Container>
    );
};
